import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { exportCsv } from '../lib/csv';
import { apiFetch, SITE_URL } from '../lib/api';
import { displayReferralCode } from '../lib/utils';
import {
  ArrowLeft, User, Phone, Copy, CheckCircle, Share2,
  GitBranch, ShoppingBag, Users, DollarSign, Gift, MinusCircle, Loader2,
  BarChart3, MousePointer, Eye, Calendar
} from 'lucide-react';

interface ReferralProfile {
  id: number;
  phone: string;
  name?: string | null;
  referralCode: string;
  totalDirectRefs: number;
  totalIndirectRefs: number;
  totalNetworkSize: number;
  totalOrderValue: number;
  totalRewardEarned?: number;
  totalRewardPaid?: number;
  totalRewardPending?: number;
  tier: string;
  createdAt?: number;
}

interface DirectReferral {
  id: number;
  referrerPhone: string;
  refereePhone: string;
  refereeOrderId?: number;
  treeDepth: number;
  rootReferrerPhone?: string;
  status: string;
  createdAt: number;
  refereeName?: string;
  orderValue?: number;
}

interface IndirectNode {
  refereePhone: string;
  treeDepth: number;
  viaReferrer: string;
  refereeName?: string;
}

interface BroughtOrder {
  orderId: number;
  orderNum: string;
  refereeName?: string;
  orderValue: number;
  paymentConfirmed: number;
  createdAt: number;
}

interface RewardHistory {
  id: number;
  profileId: number;
  phone: string;
  sourceOrderId: number;
  sourceRecordId?: number;
  rewardType: string;
  amount: number;
  description?: string;
  status: string;
  approvedAt?: number;
  paidAt?: number;
  paidBy?: number;
  createdAt: number;
  sourceOrderNum?: string;
}

const TIER_STYLES: Record<string, string> = {
  bronze: 'bg-amber-100 text-amber-700',
  silver: 'bg-gray-100 text-gray-700',
  gold: 'bg-yellow-100 text-yellow-700',
  platinum: 'bg-purple-100 text-purple-700',
};

const TIER_LABELS: Record<string, string> = {
  bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum',
};

const STATUS_BADGES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

function formatDollars(dollars = 0): string {
  return `HK$${Math.round(dollars).toLocaleString('zh-HK')}`;
}
function formatCents(cents = 0): string {
  return `HK$${Math.round(cents / 100).toLocaleString('zh-HK')}`;
}

function formatDate(ts?: number): string {
  if (!ts) return '-';
  const d = new Date(ts * 1000);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('zh-HK');
}

function formatDateTime(ts?: number): string {
  if (!ts) return '-';
  const d = new Date(ts * 1000);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString('zh-HK', { hour: '2-digit', minute: '2-digit' });
}

type TabId = 'direct' | 'indirect' | 'orders' | 'rewards' | 'traffic';
// 型別對齊 src/types（base 內為 Referral* 前綴）
type TrafficStats = import('../types').ReferralTrafficStats;
type DailyTrend = import('../types').ReferralDailyTrend;
type TopPage = import('../types').ReferralTopPage;

const TABS = [
  { id: 'direct' as TabId, label: '直接推薦', icon: Users },
  { id: 'indirect' as TabId, label: '間接網絡', icon: GitBranch },
  { id: 'orders' as TabId, label: '帶來的訂單', icon: ShoppingBag },
  { id: 'rewards' as TabId, label: '獎勵記錄', icon: Gift },
  { id: 'traffic' as TabId, label: '流量追蹤', icon: BarChart3 },
];

const AdminReferralDetail: React.FC = () => {
  const { phone } = useParams<{ phone: string }>();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<ReferralProfile | null>(null);
  const [direct, setDirect] = useState<DirectReferral[]>([]);
  const [indirect, setIndirect] = useState<IndirectNode[]>([]);
  const [orders, setOrders] = useState<BroughtOrder[]>([]);
  const [rewards, setRewards] = useState<RewardHistory[]>([]);
  const [traffic, setTraffic] = useState<TrafficStats | null>(null);
  const [dailyTrend, setDailyTrend] = useState<DailyTrend[]>([]);
  const [topPages, setTopPages] = useState<TopPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('direct');
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeemDescription, setRedeemDescription] = useState('');
  const [redeemReference, setRedeemReference] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [trafficDays, setTrafficDays] = useState(30);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (!phone) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(`/api/public/admin/referrals/${encodeURIComponent(phone)}?days=${trafficDays}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || '獲取詳情失敗');
        }
        const json = await res.json();
        if (cancelled) return;
        setProfile(json.profile || null);
        setDirect(Array.isArray(json.directReferrals) ? json.directReferrals : []);
        setIndirect(Array.isArray(json.indirectNetwork) ? json.indirectNetwork : []);
        setOrders(Array.isArray(json.broughtOrders) ? json.broughtOrders : []);
        setRewards(Array.isArray(json.rewardHistory) ? json.rewardHistory : []);
        setTraffic(json.traffic || null);
        setDailyTrend(Array.isArray(json.dailyTrend) ? json.dailyTrend : []);
        setTopPages(Array.isArray(json.topPages) ? json.topPages : []);
      } catch (e: any) {
        if (!cancelled) setError(e.message || '加載失敗');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [phone, trafficDays]);

  useEffect(() => {
    if (profile?.referralCode && typeof window !== 'undefined') {
      setShareUrl(`${SITE_URL}/?r=${encodeURIComponent(displayReferralCode(profile.referralCode))}`);
    }
  }, [profile?.referralCode]);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  const pendingReward = Math.max(0, (profile?.totalRewardEarned || 0) - (profile?.totalRewardPaid || 0));

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    const dollars = parseFloat(redeemAmount);
    if (!dollars || dollars <= 0) {
      setRedeemError('請輸入有效金額');
      return;
    }
    const cents = Math.round(dollars * 100);
    if (cents > pendingReward) {
      setRedeemError('對銷金額不能超過待對銷餘額');
      return;
    }
    setRedeemLoading(true);
    setRedeemError(null);
    try {
      const res = await apiFetch(`/api/admin/referrals/${profile.id}/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: cents, description: redeemDescription.trim() || '管理員對銷', reference: redeemReference.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '對銷失敗');
      setShowRedeemModal(false);
      setRedeemAmount('');
      setRedeemDescription('');
      setRedeemReference('');
      // Refresh profile and reward records
      const profileRes = await apiFetch(`/api/public/admin/referrals/${encodeURIComponent(profile.phone)}`);
      if (profileRes.ok) {
        const json = await profileRes.json();
        setProfile(json.profile || profile);
        setDirect(Array.isArray(json.directReferrals) ? json.directReferrals : []);
        setIndirect(Array.isArray(json.indirectNetwork) ? json.indirectNetwork : []);
        setOrders(Array.isArray(json.broughtOrders) ? json.broughtOrders : []);
        setRewards(Array.isArray(json.rewardHistory) ? json.rewardHistory : []);
      }
    } catch (e: any) {
      setRedeemError(e.message || '對銷失敗');
    } finally {
      setRedeemLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="referrals" onLogout={handleLogout}>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">加載中...</div>
      </AdminLayout>
    );
  }

  if (error || !profile) {
    return (
      <AdminLayout currentPage="referrals" onLogout={handleLogout}>
        <div className="text-center text-red-500 py-12">{error || '推薦人不存在'}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="referrals" onLogout={handleLogout}>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/referrals')}
          className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-4 h-4" /> 返回推薦網絡
        </button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-red-600 dark:text-red-400 text-xl font-bold">
              {profile.name?.charAt(0) || '?'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{profile.name || '匿名'}</h1>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TIER_STYLES[profile.tier?.toLowerCase()] || TIER_STYLES.bronze}`}>
                  {TIER_LABELS[profile.tier?.toLowerCase()] || profile.tier}
                </span>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {profile.phone}</span>
                <span className="flex items-center gap-1"><Share2 className="w-3 h-3" /> {displayReferralCode(profile.referralCode)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{shareUrl || '-'}</span>
              <button
                onClick={handleCopy}
                disabled={!shareUrl}
                className="text-xs flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-200 disabled:opacity-50"
              >
                {copied ? <CheckCircle className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                {copied ? '已複製' : '複製'}
              </button>
            </div>
            <button
              onClick={() => navigate(`/admin/referrals/${encodeURIComponent(profile.phone)}/tree`)}
              className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
            >
              <GitBranch className="w-4 h-4" /> 鏈路圖
            </button>
          </div>
        </div>
      </div>

      {/* Redeem Action */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">待對銷獎勵</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{formatCents(pendingReward)}</div>
          <div className="text-xs text-gray-400 mt-1">
            已累積 {formatCents(profile.totalRewardEarned || 0)} · 已對銷 {formatCents(profile.totalRewardPaid || 0)}
          </div>
        </div>
        <button
          onClick={() => setShowRedeemModal(true)}
          disabled={pendingReward <= 0}
          className="inline-flex items-center gap-1 bg-red-600 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          <MinusCircle className="w-4 h-4" /> 對銷獎勵
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">直接推薦</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-red-600" />
            {profile.totalDirectRefs}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">間接推薦</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-blue-600" />
            {profile.totalIndirectRefs}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">網絡總人數</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-green-600" />
            {profile.totalNetworkSize}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">帶來訂單金額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-purple-600" />
            {formatCents(profile.totalOrderValue)}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-2 px-2 md:space-x-6 md:px-4 overflow-x-auto" aria-label="分頁">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-1 transition ${
                    activeTab === tab.id
                      ? 'border-red-500 text-red-600 dark:text-red-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-4">
          {activeTab === 'direct' && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  <tr>
                    <th className="px-4 py-3">被推薦人</th>
                    <th className="px-4 py-3">訂單號</th>
                    <th className="px-4 py-3 text-right">訂單金額</th>
                    <th className="px-4 py-3">時間</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {direct.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">暫無直接推薦</td></tr>
                  ) : (
                    direct.map((d) => (
                      <tr key={d.id} className="hover:bg-red-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800 dark:text-white">{d.refereeName || '匿名'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{d.refereePhone}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono">{d.refereeOrderId || '-'}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-white">{formatCents(d.orderValue)}</td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(d.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'indirect' && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  <tr>
                    <th className="px-4 py-3">被推薦人</th>
                    <th className="px-4 py-3">鏈路深度</th>
                    <th className="px-4 py-3">經由誰</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {indirect.length === 0 ? (
                    <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">暫無間接網絡</td></tr>
                  ) : (
                    indirect.map((node, idx) => (
                      <tr key={`${node.refereePhone}-${idx}`} className="hover:bg-red-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800 dark:text-white">{node.refereeName || '匿名'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{node.refereePhone}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">{node.treeDepth}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 font-mono">{node.viaReferrer}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  <tr>
                    <th className="px-4 py-3">訂單號</th>
                    <th className="px-4 py-3">被推薦人</th>
                    <th className="px-4 py-3 text-right">金額</th>
                    <th className="px-4 py-3">付款狀態</th>
                    <th className="px-4 py-3">時間</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {orders.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">暫無帶來的訂單</td></tr>
                  ) : (
                    orders.map((o) => (
                      <tr key={o.orderId} className="hover:bg-red-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 font-mono text-gray-800 dark:text-white">{o.orderNum}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800 dark:text-white">{o.refereeName || '匿名'}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-white">{formatCents(o.orderValue)}</td>
                        <td className="px-4 py-3">
                          {o.paymentConfirmed ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">已付款</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">待付款</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(o.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'rewards' && (
            <div className="overflow-x-auto">
              <div className="flex justify-end px-4 py-2">
                <button
                  onClick={() =>
                    exportCsv(`referral-rewards-${profile?.phone || 'export'}`, [
                      ['類型', '金額(HKD)', '來源訂單', '狀態', '說明', '時間'],
                      ...rewards.map((r) => [
                        r.rewardType,
                        (r.amount / 100).toFixed(2),
                        r.sourceOrderNum || r.sourceOrderId || '-',
                        r.status,
                        r.description || '',
                        r.createdAt ? new Date(r.createdAt * 1000).toLocaleString('zh-HK') : '',
                      ]),
                    ])
                  }
                  disabled={rewards.length === 0}
                  className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
                >
                  ⬇ 匯出 CSV
                </button>
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  <tr>
                    <th className="px-4 py-3">類型</th>
                    <th className="px-4 py-3 text-right">金額</th>
                    <th className="px-4 py-3">來源訂單</th>
                    <th className="px-4 py-3">狀態</th>
                    <th className="px-4 py-3">時間</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rewards.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">暫無獎勵記錄</td></tr>
                  ) : (
                    rewards.map((r) => (
                      <tr key={r.id} className="hover:bg-red-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">{r.rewardType}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-white">{formatCents(r.amount)}</td>
                        <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-300">{r.sourceOrderNum || r.sourceOrderId || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGES[r.status] || 'bg-gray-100 text-gray-700'}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'traffic' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300">最近表現</h4>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-500 dark:text-gray-400" />
                  <select
                    value={trafficDays}
                    onChange={(e) => setTrafficDays(Number(e.target.value))}
                    className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-red-500 outline-none"
                  >
                    <option value={7}>最近 7 天</option>
                    <option value={30}>最近 30 天</option>
                    <option value={90}>最近 90 天</option>
                  </select>
                </div>
              </div>

              {!traffic ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">暫無流量數據</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 text-center">
                      <MousePointer className="mx-auto text-red-600 mb-1" size={22} />
                      <p className="text-xl font-bold text-gray-800 dark:text-white">{traffic.clicks}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">連結獨立點擊</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 text-center">
                      <Eye className="mx-auto text-red-600 mb-1" size={22} />
                      <p className="text-xl font-bold text-gray-800 dark:text-white">{traffic.uniqueVisitors}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">獨立訪客</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 text-center">
                      <ShoppingBag className="mx-auto text-red-600 mb-1" size={22} />
                      <p className="text-xl font-bold text-gray-800 dark:text-white">{traffic.orders}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">成交訂單</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 text-center">
                      <DollarSign className="mx-auto text-red-600 mb-1" size={22} />
                      <p className="text-xl font-bold text-gray-800 dark:text-white">{formatCents(traffic.revenue)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">帶來營收</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">轉化漏斗</h4>
                    <div className="space-y-2">
                      {[
                        { label: '獨立點擊', value: traffic.clicks, color: 'bg-blue-500' },
                        { label: '瀏覽頁面', value: traffic.pageViews, color: 'bg-indigo-500' },
                        { label: '加入購物車', value: traffic.addToCarts, color: 'bg-purple-500' },
                        { label: '開始結帳', value: traffic.beginCheckouts, color: 'bg-pink-500' },
                        { label: '完成訂單', value: traffic.orders, color: 'bg-green-500' },
                      ].map((step, idx, arr) => {
                        const max = Math.max(1, arr[0].value);
                        const prev = idx > 0 ? arr[idx - 1].value : step.value;
                        const rate = prev ? ((step.value / prev) * 100).toFixed(1) + '%' : '0%';
                        return (
                          <div key={step.label} className="flex items-center gap-3">
                            <div className="w-24 text-xs text-gray-500 dark:text-gray-400 text-right">{step.label}</div>
                            <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden relative">
                              <div className={`h-full ${step.color} transition-all duration-500`} style={{ width: `${Math.min(100, (step.value / max) * 100)}%` }} />
                              <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-gray-800 dark:text-gray-100">
                                {step.value.toLocaleString('zh-HK')} {idx > 0 && <span className="ml-2 text-gray-400 dark:text-gray-500">({rate})</span>}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
                      <span>點擊轉化率：<span className="font-bold text-red-600">{traffic.clickToOrderRate}</span></span>
                      <span>訪客轉化率：<span className="font-bold text-red-600">{traffic.visitorToOrderRate}</span></span>
                    </div>
                  </div>

                  {dailyTrend.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">每日趨勢</h4>
                      <div className="overflow-x-auto">
                        <div className="min-w-[600px] h-40 flex items-end gap-1">
                          {dailyTrend.map((day) => {
                            const max = Math.max(1, ...dailyTrend.map(d => d.clicks));
                            return (
                              <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                                <div className="w-full flex items-end gap-0.5 h-28">
                                  <div className="flex-1 bg-blue-200 dark:bg-blue-900 rounded-t" style={{ height: `${(day.clicks / max) * 100}%` }} title={`獨立點擊 ${day.clicks}`} />
                                  <div className="flex-1 bg-green-300 dark:bg-green-700 rounded-t" style={{ height: `${(day.orders / max) * 100}%` }} title={`訂單 ${day.orders}`} />
                                </div>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400 rotate-45 origin-left translate-y-2">{day.date.slice(5)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-6 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-200 dark:bg-blue-900 rounded" /> 點擊</span>
                        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-300 dark:bg-green-700 rounded" /> 訂單</span>
                      </div>
                    </div>
                  )}

                  {topPages.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">熱門頁面</h4>
                      <div className="space-y-2">
                        {topPages.map((page, idx) => (
                          <div key={page.page + idx} className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-300 truncate max-w-[70%]">{page.page || '/'}</span>
                            <span className="font-medium text-gray-800 dark:text-white">{page.views} 次瀏覽</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Redeem Modal */}
      {showRedeemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <MinusCircle size={20} /> 對銷獎勵
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              目前待對銷餘額：<span className="font-bold text-gray-800 dark:text-white">{formatCents(pendingReward)}</span>
            </p>
            <form onSubmit={handleRedeem} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">對銷金額（HK$）</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={pendingReward / 100}
                  required
                  value={redeemAmount}
                  onChange={(e) => setRedeemAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">付款參考（FPS / 轉帳參考號，會發送俾夥伴）</label>
                <input
                  type="text"
                  value={redeemReference}
                  onChange={(e) => setRedeemReference(e.target.value)}
                  placeholder="例如：FPS654321 / May 2024 月結"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">備註</label>
                <textarea
                  rows={2}
                  value={redeemDescription}
                  onChange={(e) => setRedeemDescription(e.target.value)}
                  placeholder="例如：轉帳兌換、購物抵扣"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white resize-none"
                />
              </div>
              {redeemError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{redeemError}</div>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowRedeemModal(false); setRedeemError(null); }}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={redeemLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {redeemLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '確認對銷'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminReferralDetail;
